import { useTranslation } from "react-i18next";
import type {
  CsvImportJobDto,
  CsvImportOptionsType,
  CsvImportSkippedRow,
  CsvSourceFormat,
  MigrationEntity,
  MigrationNotice,
} from "../../api/migrationsApi";
import { migrationNoticeText } from "../migrations/MigrationReport";

/**
 * The pure parts of the CSV import modal (AKL-813): which columns each format expects, the
 * per-entity copy, the template download, the request options, the "may hand off to a full
 * migration" gate, and the sentence for every notice and skip code the server can send.
 *
 * Kept free of React so they can be unit-tested directly and reused by the locations and
 * deployments imports (AKL-814/815) without touching the modal. The registries are keyed by
 * entity first and format second. They stay typed `Partial` even now that all three
 * entities are populated: not every entity offers every format (locations has no CTMS
 * layout), and the accessors throw on a missing entry so a wrong `entity` prop — or a value
 * outside the union arriving from an older or newer server — fails loudly in development
 * rather than rendering an empty modal.
 */

/** One expected column of a CSV format. Strings are translation keys; `t()` is applied later. */
export type CsvColumnSpec = {
  name: string;
  required: boolean;
  /** A short hint shown beside the column name, when the name alone is not enough. */
  hint?: string;
  /**
   * Interpolation values for `hint`, when the hint quotes runtime data (AKL-815: a template
   * field's allowed options). The hint itself stays a fixed catalog key; template data only
   * ever reaches `t()` as a value, never as a key.
   */
  hintParams?: Record<string, string>;
};

/**
 * The columns each (entity, format) pair expects. The import reads columns by name, so the
 * order here matters only as the header row of the downloadable template: akili's own layout
 * for the Akili format, Wildlife Insights' own file layout for the CTMS format (cameras.csv
 * for sensors, deployments.csv for deployments).
 */
export const CSV_IMPORT_COLUMNS: Partial<
  Record<MigrationEntity, Partial<Record<CsvSourceFormat, CsvColumnSpec[]>>>
> = {
  sensors: {
    akili_csv: [
      { name: "sensor_name", required: true },
      {
        name: "sensor_type",
        required: false,
        hint: "Leave blank for a camera",
      },
      { name: "make", required: false },
      { name: "model", required: false },
      { name: "serial_number", required: false },
      {
        name: "purchase_date",
        required: false,
        hint: "YYYY-MM-DD, or just the year",
      },
      { name: "purchase_price", required: false },
      { name: "product_url", required: false },
      { name: "comments", required: false },
    ],
    ctms_csv: [
      {
        name: "project_id",
        required: false,
        hint: "Ignored: sensors belong to the organisation",
      },
      { name: "camera_id", required: true },
      { name: "camera_name", required: false },
      { name: "make", required: false },
      { name: "model", required: false },
      { name: "serial_number", required: false },
      { name: "year_purchased", required: false },
    ],
  },
  // AKL-814. One format only: Wildlife Insights has no locations file, so there is no CTMS
  // layout to offer. The order is the API's template order.
  locations: {
    akili_csv: [
      { name: "location_name", required: true },
      { name: "latitude", required: true, hint: "Decimal degrees" },
      { name: "longitude", required: true, hint: "Decimal degrees" },
      {
        name: "country_id",
        required: false,
        hint: "Two-letter country code, for example KE. Blank uses the project's country.",
      },
    ],
  },
  // AKL-815. Both formats. The Akili layout is the six fixed columns; the columns a
  // project's deployment template adds are not known until the project is loaded, so the
  // modal appends them at runtime (`schemaColumns`). The CTMS layout is Wildlife Insights'
  // bulk-upload deployments.csv in WI's own order; the API also tolerates the extra columns
  // an export-vintage deployments.csv carries, which a template need not list.
  deployments: {
    akili_csv: [
      { name: "deployment_name", required: true },
      {
        name: "location_name",
        required: true,
        hint: "Must match a location already in this project",
      },
      {
        name: "sensor_name",
        required: false,
        hint: "Must match a sensor already in this organisation. Blank means no sensor.",
      },
      { name: "start_date", required: false, hint: "YYYY-MM-DD" },
      { name: "end_date", required: false, hint: "YYYY-MM-DD" },
      {
        name: "timezone",
        required: false,
        hint: "IANA time zone, for example Africa/Nairobi. Blank uses the project's time zone.",
      },
    ],
    ctms_csv: [
      {
        name: "project_id",
        required: false,
        hint: "Ignored: the project is chosen in akili",
      },
      { name: "deployment_id", required: true },
      { name: "subproject_name", required: false },
      { name: "subproject_design", required: false },
      { name: "placename", required: false, hint: "Blank uses deployment_id" },
      { name: "longitude", required: false, hint: "Decimal degrees" },
      { name: "latitude", required: false, hint: "Decimal degrees" },
      { name: "start_date", required: false },
      { name: "end_date", required: false },
      { name: "event_name", required: false },
      { name: "event_description", required: false },
      { name: "event_type", required: false },
      { name: "bait_type", required: false },
      { name: "bait_description", required: false },
      { name: "feature_type", required: false },
      { name: "feature_type_methodology", required: false },
      {
        name: "camera_id",
        required: false,
        hint: "Must match a sensor's serial number or name in this organisation",
      },
      { name: "quiet_period", required: false },
      { name: "camera_functioning", required: false },
      { name: "sensor_height", required: false },
      { name: "height_other", required: false },
      { name: "sensor_orientation", required: false },
      { name: "orientation_other", required: false },
      { name: "recorded_by", required: false },
    ],
  },
};

/**
 * The column spec for one entity and format.
 * @param entity what the file contains
 * @param format how it is laid out
 * @param extra runtime columns appended after the fixed ones (AKL-815: the columns a
 *   project's deployment template adds to the Akili layout); none by default
 * @returns the expected columns, in template order
 * @throws when no spec is registered for that pair
 */
export const columnsFor = (
  entity: MigrationEntity,
  format: CsvSourceFormat,
  extra: CsvColumnSpec[] = [],
): CsvColumnSpec[] => {
  const columns = CSV_IMPORT_COLUMNS[entity]?.[format];
  if (!columns) {
    throw new Error(`No CSV column spec for ${entity} in ${format}`);
  }
  return [...columns, ...extra];
};

/** The property types a deployment template column can carry into a CSV cell. */
const SCHEMA_COLUMN_TYPES = new Set(["string", "number", "integer", "boolean"]);

/** Is this a plain object (not null, not an array)? */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The optional columns a project's deployment template adds to the Akili deployments layout
 * (AKL-815). Mirrors the API's `template_columns` rule so the modal's column list and the
 * downloadable template match what the server reads: one column per property of
 * `schema.properties` whose value is a non-empty `enum` list or whose `type` is string,
 * number, integer or boolean (array- and object-typed properties cannot live in a cell);
 * ordered by `ui_schema["ui:order"]` for the keys it names, then the remaining properties in
 * declaration order. The column name is the API's header — the key trimmed and lower-cased,
 * the way the server normalises every CSV header — and a name that would collide with a
 * fixed Akili column or an earlier template column is dropped (first wins). No column is
 * required: the API does not enforce a template's `required` list.
 *
 * Pure and defensive: `[]` for anything that is not an object with an object `properties`,
 * so a project with no template, a stale server or a malformed schema all render as "no
 * template columns" rather than a crash. Hints are fixed catalog keys; an enum's options
 * reach `t()` only as an interpolation value.
 * @param schema the project's `deployment_schema` (a JSON Schema object, or anything else)
 * @param uiSchema the project's `ui_schema`, read for `ui:order` only
 * @returns the template columns, in the API's template order
 */
export const schemaColumns = (
  schema: unknown,
  uiSchema?: unknown,
): CsvColumnSpec[] => {
  if (!isRecord(schema) || !isRecord(schema.properties)) return [];
  const properties = schema.properties;
  const order = isRecord(uiSchema) ? uiSchema["ui:order"] : undefined;
  const ordered: string[] = [];
  const seenKeys = new Set<string>();
  if (Array.isArray(order)) {
    for (const key of order) {
      if (typeof key === "string" && key in properties && !seenKeys.has(key)) {
        ordered.push(key);
        seenKeys.add(key);
      }
    }
  }
  for (const key of Object.keys(properties)) {
    if (!seenKeys.has(key)) {
      ordered.push(key);
      seenKeys.add(key);
    }
  }

  const fixedNames = new Set(
    (CSV_IMPORT_COLUMNS.deployments?.akili_csv ?? []).map((c) => c.name),
  );
  const taken = new Set<string>();
  const columns: CsvColumnSpec[] = [];
  for (const key of ordered) {
    const property = properties[key];
    if (!isRecord(property)) continue;
    const name = key.trim().toLowerCase();
    if (name === "" || fixedNames.has(name) || taken.has(name)) continue;
    const options = Array.isArray(property.enum)
      ? property.enum.filter(
          (option) => option !== null && option !== undefined,
        )
      : [];
    if (options.length > 0) {
      taken.add(name);
      columns.push({
        name,
        required: false,
        hint: "One of: {{options}}",
        hintParams: { options: options.map(String).join(", ") },
      });
      continue;
    }
    const type = property.type;
    if (typeof type !== "string" || !SCHEMA_COLUMN_TYPES.has(type)) continue;
    taken.add(name);
    const column: CsvColumnSpec = { name, required: false };
    if (type === "number") column.hint = "A number";
    else if (type === "integer") column.hint = "A whole number";
    else if (type === "boolean") column.hint = "true or false";
    columns.push(column);
  }
  return columns;
};

/** The entity-specific wording of the modal, as translation keys. */
export type CsvEntityCopy = {
  /** Dialog title. */
  title: string;
  /**
   * The question on the fork step, when the WI format could also mean a full migration.
   * Present only for an entity offered in a format that can mean a full Wildlife Insights
   * export (sensors, via cameras.csv); absent means the fork is never shown for the entity.
   */
  forkQuestion?: string;
  /** The fork button that continues with just this entity. Present with `forkQuestion`. */
  forkImportOnly?: string;
  /** The confirm button on a planned job. */
  confirm: string;
  /** Shown beside the spinner while the apply runs. */
  applying: string;
  /** Fallback when the apply request fails without a usable detail. */
  applyFailure: string;
  /**
   * Shown when polling gives up. Names the list to check, because a closed dialog cannot be
   * reopened to the same job: there is no organisation-level import history (AKL-813).
   */
  pollFailure: string;
  /** The row label in the report — the same key the app already uses for the entity. */
  label: string;
};

export const ENTITY_COPY: Partial<Record<MigrationEntity, CsvEntityCopy>> = {
  sensors: {
    title: "Import sensors from CSV",
    forkQuestion:
      "Import only the sensors in this file, or migrate a full Wildlife Insights export?",
    forkImportOnly: "Import sensors only",
    confirm: "Import these sensors",
    applying: "Importing sensors…",
    applyFailure: "akili could not import these sensors.",
    pollFailure:
      "akili lost track of this import. It may still be running — wait a minute, then close this window and check the sensors list.",
    label: "Sensors",
  },
  // AKL-814. No fork strings: Wildlife Insights has no locations file, so a locations CSV
  // can never be the start of a full migration.
  locations: {
    title: "Import locations from CSV",
    confirm: "Import these locations",
    applying: "Importing locations…",
    applyFailure: "akili could not import these locations.",
    pollFailure:
      "akili lost track of this import. It may still be running — wait a minute, then close this window and check the locations list.",
    label: "Locations",
  },
  // AKL-815. Fork strings present: a Wildlife Insights deployments.csv usually comes out of
  // a full export, so the CTMS format may mean a full migration into this project.
  deployments: {
    title: "Import deployments from CSV",
    forkQuestion:
      "Import only the deployments in this file, or migrate a full Wildlife Insights export?",
    forkImportOnly: "Import deployments only",
    confirm: "Import these deployments",
    applying: "Importing deployments…",
    applyFailure: "akili could not import these deployments.",
    pollFailure:
      "akili lost track of this import. It may still be running — wait a minute, then close this window and check the deployments list.",
    label: "Deployments",
  },
};

/**
 * The copy for one entity.
 * @param entity what the file contains
 * @returns the translation keys for that entity's modal
 * @throws when no copy is registered for the entity
 */
export const entityCopy = (entity: MigrationEntity): CsvEntityCopy => {
  const copy = ENTITY_COPY[entity];
  if (!copy) throw new Error(`No CSV import copy for ${entity}`);
  return copy;
};

/** The radio label for each format, as translation keys. */
export const FORMAT_LABEL: Record<CsvSourceFormat, string> = {
  akili_csv: "Akili format",
  ctms_csv: "Wildlife Insights (CTMS) format",
};

/**
 * The file name of the downloadable template. The WI one keeps Wildlife Insights' own
 * `cameras` prefix so the bundle walker still recognises the file if it is dropped into a
 * WI export folder.
 * @param entity what the file contains
 * @param format how it is laid out
 * @returns a `.csv` file name
 */
export const templateFileName = (
  entity: MigrationEntity,
  format: CsvSourceFormat,
): string => {
  if (format === "ctms_csv" && entity === "sensors")
    return "cameras-template.csv";
  return `${entity}-template.csv`;
};

/**
 * One CSV cell, quoted the RFC 4180 way when it has to be. A value is returned unchanged
 * unless it contains a comma, a double quote, a carriage return or a line feed; then it is
 * wrapped in double quotes with every embedded double quote doubled. The fixed column names
 * never need this, but a template property key is any string JSON Schema allows (AKL-815),
 * and an unquoted `habitat,detail` would split into two header cells on the way back in. The
 * API reads the header with Python's `csv` module, which honours this quoting, so a quoted
 * cell round-trips as one header.
 * @param value the cell's text
 * @returns the text as it should appear in the CSV
 */
export const csvCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * The template's contents: a UTF-8 byte-order mark (so Excel opens it as UTF-8 rather than
 * guessing) and the header row, terminated the way Excel writes CSVs. Every header cell,
 * fixed or extra, goes through `csvCell`, so a template column name that holds a comma, a
 * quote or a line break still reads back as one column.
 * @param entity what the file contains
 * @param format how it is laid out
 * @param extra runtime columns appended after the fixed ones (AKL-815); none by default
 * @returns the CSV text
 */
export const templateCsv = (
  entity: MigrationEntity,
  format: CsvSourceFormat,
  extra: CsvColumnSpec[] = [],
): string =>
  "\uFEFF" +
  columnsFor(entity, format, extra)
    .map((column) => csvCell(column.name))
    .join(",") +
  "\r\n";

/**
 * A `data:` URL for the template, so a plain `<a download>` serves it with no library and no
 * `URL.createObjectURL` (which jsdom lacks and which needs revoking).
 * @param entity what the file contains
 * @param format how it is laid out
 * @param extra runtime columns appended after the fixed ones (AKL-815); none by default
 * @returns the href
 */
export const templateHref = (
  entity: MigrationEntity,
  format: CsvSourceFormat,
  extra: CsvColumnSpec[] = [],
): string =>
  "data:text/csv;charset=utf-8," +
  encodeURIComponent(templateCsv(entity, format, extra));

/**
 * The options a CSV import posts. `target_project_id` is left off entirely when there is
 * none — an organisation-level import must not say "project: null", and the API treats any
 * project field on a sensors import as a validation error.
 * @param orgId the organisation the file belongs to
 * @param entity what the file contains
 * @param format how it is laid out
 * @param targetProjectId the project, for the project-scoped imports of AKL-814/815
 * @returns the options object to send
 */
export const buildCsvImportOptions = (
  orgId: number,
  entity: MigrationEntity,
  format: CsvSourceFormat,
  targetProjectId?: number | null,
): CsvImportOptionsType => {
  const options: CsvImportOptionsType = {
    org_id: orgId,
    source_format: format,
    entities: [entity],
  };
  if (targetProjectId !== null && targetProjectId !== undefined) {
    options.target_project_id = targetProjectId;
  }
  return options;
};

/** The notice a deployments import emits when it puts a deployment template on the project. */
const TEMPLATE_APPLIED_CODE = "deployment_template_applied";

/**
 * Did this applied import put a deployment template on its project (AKL-815)?
 *
 * The Wildlife Insights deployments format copies the CTMS template onto a project that had
 * none, and the report says so with a `deployment_template_applied` warning. A container that
 * holds the project's template in state — the Deployments tab's `deploymentSchema` prop, the
 * project page's `originalProject` — is stale from that moment: the next import dialog would
 * still say no template columns can be read and offer a download without the new columns. The
 * two containers call this on the job they were handed and, when it says yes, ask the owner
 * of the project state to re-read it (`onTemplateApplied`).
 *
 * Reads the `result` (what the apply did) and falls back to the `plan` for a job whose result
 * is not stored: the plan's outcome is the apply's, since the same user runs both. Only an
 * `applied` job counts — a planned job's notice reads "will be applied", and nothing has
 * changed yet. Defensive about shape, like `isCsvImportPlan`: a report without a warnings
 * list is "no".
 * @param job the import job, as delivered to `onImported` or read back on settle
 * @returns true when the project's deployment template changed
 */
export const appliedTemplate = (job: CsvImportJobDto): boolean => {
  if (job.status !== "applied") return false;
  const report = job.result ?? job.plan;
  const warnings: unknown = report?.warnings;
  return (
    Array.isArray(warnings) &&
    warnings.some(
      (warning) => isRecord(warning) && warning.code === TEMPLATE_APPLIED_CODE,
    )
  );
};

/**
 * May this user be offered "Migrate full export" — the hand-off to the AKL-812 deep link?
 *
 * Mirrors `canOpenWildlifeInsightsMigration` in create mode: an admin, or someone holding
 * `CREATE_PROJECT_IN_ORG`. The gate is load-bearing rather than cosmetic. The deep link
 * `/organizations/<guid>/addProject?migrate=wi` always opens the WI modal, because the New
 * Project page pushes the guid's organisation into its options list for anyone who arrives
 * there; a user without project-creation rights would then upload a bundle and receive a 403
 * for nothing (until AKL-939 the interceptor also signed them out for it). Dormant today —
 * every organisation role that
 * holds `CREATE_SENSORS_IN_ORG` also holds `CREATE_PROJECT_IN_ORG` — but it must not be
 * dropped when that stops being true.
 * @param perms the user's organisation permissions
 * @param isAdmin whether the user is a platform admin
 * @returns true when the fork may offer the full migration
 */
export const canMigrateFullExport = (
  perms: { name: string }[],
  isAdmin: boolean,
): boolean =>
  isAdmin || perms.some((perm) => perm.name.includes("CREATE_PROJECT_IN_ORG"));

/** The same `t` type `migrationNoticeText` takes, so the CSV text can delegate to it. */
type TFunction = ReturnType<typeof useTranslation>["t"];

/**
 * A `columns`-style parameter as display text: the server sends a list, but a stored report
 * (or an older server) may hold a single string.
 * @param value the raw parameter
 * @returns the column names joined for display
 */
const columnList = (value: unknown): string =>
  Array.isArray(value) ? value.map(String).join(", ") : String(value ?? "");

/**
 * One CSV-import notice as a sentence in the reader's language.
 *
 * Codes the CSV import shares with a WI migration fall through to `migrationNoticeText`, so an
 * unknown code still renders as its code rather than a blank or a crash — a silently dropped
 * blocking notice would leave the user staring at a disabled button. `purchase_year_only`
 * keeps the AKL-747 sentence (which names Wildlife Insights and "Camera") for a CTMS file and
 * uses a media-generic one for an Akili file, chosen by the notice's `source` parameter;
 * `month_first_assumed` branches on `source` the same way (AKL-815). `project_id_ignored`
 * branches on the notice's `entity`: a deployments file goes into a project the user chose,
 * a sensors file into the organisation.
 * @param notice the code and parameters from the report
 * @param t the translation function
 * @returns the sentence to display
 */
export const csvImportNoticeText = (
  notice: MigrationNotice,
  t: TFunction,
): string => {
  const params = notice.params ?? {};
  switch (notice.code) {
    case "wrong_delimiter":
      return params.delimiter === "tab"
        ? t(
            "This file uses tabs to separate columns. Save it as a comma-separated CSV and try again.",
          )
        : t(
            "This file uses semicolons to separate columns. Save it as a comma-separated CSV and try again.",
          );
    case "duplicate_columns":
      return t(
        "The column header repeats a name, so akili cannot tell the columns apart: {{columns}}",
        { columns: columnList(params.columns) },
      );
    case "required_columns_missing":
      return t("The column header is missing a required column: {{columns}}", {
        columns: columnList(params.columns),
      });
    case "too_many_rows":
      return t(
        "This file has more than {{limit}} rows. Split it into smaller files and import each one.",
        params,
      );
    case "no_rows":
      return t("This file has a header row but no data rows.");
    case "csv_unreadable":
      return t(
        "This file could not be read past row {{row}}. Check for an unclosed quotation mark near there.",
        params,
      );
    case "columns_ignored":
      return t(
        "The column header has columns akili does not read, so they were ignored: {{columns}}",
        { columns: columnList(params.columns) },
      );
    case "project_id_ignored":
      // A deployments file (AKL-815) is imported into a project the user has already chosen,
      // so the sensors reason ("belong to the organisation") would be wrong for it.
      if (params.entity === "deployments") {
        return t(
          "project_id was ignored on {{count}} rows: the project is the one you chose in akili.",
          params,
        );
      }
      return t(
        "project_id was ignored on {{count}} rows: sensors belong to the organisation, not to a project.",
        params,
      );
    case "month_first_assumed":
      // The WI sentence calls month-first "the Wildlife Insights convention", which is right
      // for a CTMS file and wrong for an Akili one (AKL-815), so the Akili file gets a
      // media-generic sentence; anything else falls through to the WI wording.
      if (params.source === "akili_csv") {
        return t(
          "Some deployment dates were not in year-month-day order, so they were read month-first (MM/DD/YYYY). Check the deployment dates afterwards.",
        );
      }
      return migrationNoticeText(notice, t);
    case "deployments_without_location":
      return t(
        "{{count}} deployments named a location akili does not have yet and could not create, so they were imported without a location. The skipped rows say why.",
        params,
      );
    case "schema_value_unknown_more":
      return t(
        "{{count}} more values are not among akili's options and were kept as-is.",
        params,
      );
    case "deployment_template_applied":
      // A CSV import's template outcome is a notice, not the `deployment_template` section a
      // WI migration report carries (AKL-817), so it needs its own sentence here.
      return t(
        "This import applies the {{template}} deployment template to “{{project_name}}” so the imported fields show in the deployment form.",
        params,
      );
    case "deployment_template_not_applied":
      // Applying a template is a project edit; a user who may create deployments but not
      // edit the project gets the import without it, and is told who can fix that.
      return t(
        "The {{template}} deployment template was not applied to “{{project_name}}”: changing a project's template needs the project-editing permission. Ask a project owner or editor to pick it on the Project Details tab so the imported fields show in the deployment form.",
        params,
      );
    case "encoding_assumed":
      return t(
        "This file was not saved as UTF-8, so it was read as {{encoding}}. Check any names with accented characters afterwards.",
        params,
      );
    case "purchase_year_only_more":
      return t(
        "{{count}} more rows gave only a year of purchase, so they were stored as 1 January of that year.",
        params,
      );
    case "purchase_year_only":
      // The AKL-747 sentence names "Camera" and "Wildlife Insights", which is right for a
      // CTMS file and wrong for an Akili one.
      if (params.source === "ctms_csv") return migrationNoticeText(notice, t);
      return t(
        "{{camera}}: only a year of purchase ({{year}}) was given, so it was stored as 1 January {{year}}.",
        params,
      );
    default:
      return migrationNoticeText(notice, t);
  }
};

/**
 * The reason one row was skipped, as a short phrase for the skipped-rows table.
 *
 * Phrases rather than sentences: they sit in a table cell after the row number. The field
 * names they quote are whatever the API sends — the user's own headers for an Akili-format
 * file (`sensor_name`, `purchase_date`; `location_name`, `latitude`, `longitude`,
 * `country_id` for a locations file, AKL-814; `start_date`, `timezone` and the template
 * columns for a deployments file, AKL-815); for a CTMS file the row-level codes name the
 * CTMS header (`camera_id`, `year_purchased`) but `field_too_long` names akili's column
 * (`sensor_name`, `serial_number`), because the length check runs on the mapped fields.
 * The deployments codes whose tail says "then this file again" are telling the user a
 * re-import is safe: rows already in akili are matched, not duplicated.
 * An unknown code renders as the code — never blank, never a throw.
 * @param entry the skipped row from the report
 * @param t the translation function
 * @returns the phrase to display
 */
export const csvSkipReasonText = (
  entry: CsvImportSkippedRow,
  t: TFunction,
): string => {
  const params = entry.params ?? {};
  switch (entry.code) {
    case "missing_required":
      return t("{{field}} is required", params);
    case "invalid_date":
      // A CTMS file's `year_purchased` is parsed as a whole number only, so telling that
      // user to write a full date would earn the row the same skip on the next upload.
      if (params.field === "year_purchased") {
        return t(
          "{{field}} “{{value}}” is not a year akili can read. Use just the year, for example 2017.",
          params,
        );
      }
      // A deployment date (AKL-815) is a day, never a bare year — unlike a purchase date,
      // where a year alone is stored as 1 January.
      if (params.field === "start_date" || params.field === "end_date") {
        return t(
          "{{field}} “{{value}}” is not a date akili can read. Use YYYY-MM-DD.",
          params,
        );
      }
      return t(
        "{{field}} “{{value}}” is not a date akili can read. Use YYYY-MM-DD, or just the year.",
        params,
      );
    case "invalid_price":
      return t(
        "{{field}} “{{value}}” is not a price akili can read. Use a number such as 249.99.",
        params,
      );
    case "field_too_long":
      return t(
        "{{field}} is {{actual}} characters, but akili allows {{limit}}",
        params,
      );
    case "unsupported_sensor_type":
      return t(
        "sensor_type “{{value}}” is not supported; leave it blank for a camera",
        params,
      );
    case "sensor_name_exists":
      return t(
        "a sensor named {{sensor_name}} already exists in this organisation",
        params,
      );
    case "duplicate_in_file":
      return t("same {{key}} as row {{first_row}}", params);
    // AKL-814 locations codes. `field` is the blank or unreadable column; `min`/`max` are the
    // API's own bounds for that axis, so the sentence never hard-codes ±90 or ±180.
    case "missing_coordinates":
      return t(
        "{{field}} is blank; a location needs both latitude and longitude",
        params,
      );
    case "invalid_coordinates":
      return t(
        "{{field}} “{{value}}” is not a coordinate akili can read. Use decimal degrees from {{min}} to {{max}}, for example -2.94.",
        params,
      );
    case "invalid_country":
      return t(
        "country_id “{{value}}” is not a two-letter country code akili recognises, for example KE. Leave it blank to use the project's country.",
        params,
      );
    case "missing_country":
      return t(
        "country_id is blank and this project has no country to fall back on",
      );
    // AKL-815 deployments codes. References are resolved by name against what akili
    // already holds, so each sentence says what to import first.
    case "location_not_found":
      return t(
        "location_name “{{value}}” is not a location in this project. Import the locations first, then this file again.",
        params,
      );
    case "sensor_not_found":
      // A CTMS camera_id is matched against the serial number first and the name second
      // (the CTMS sensors import writes both from camera_id when WI left them blank), and
      // its fix is the cameras file or the full export; an Akili sensor_name is a name.
      if (params.field === "camera_id") {
        return t(
          "camera_id “{{value}}” does not match a sensor's serial number or name in this organisation. Import cameras.csv first or migrate the full export, then this file again.",
          params,
        );
      }
      return t(
        "sensor_name “{{value}}” is not a sensor in this organisation. Import the sensors first, then this file again.",
        params,
      );
    case "invalid_timezone":
      return t(
        "timezone “{{value}}” is not a time zone akili recognises, for example Africa/Nairobi. Leave it blank to use the project's time zone.",
        params,
      );
    // AKL-1082: the row's end falls on an earlier day than its start. Both cells come back
    // as the file wrote them, so the sentence names the dates the user typed.
    case "end_before_start":
      return t(
        "end_date “{{end_date}}” is before start_date “{{start_date}}”. Correct one of the dates, then import this file again.",
        params,
      );
    case "invalid_schema_value":
      // `expected` is the template column's kind; `allowed`/`min`/`max` are the API's own
      // values for that field, so the sentence never hard-codes a template's options.
      switch (params.expected) {
        case "one_of":
          return t(
            "{{field}} “{{value}}” is not one of this project's options for that field: {{allowed}}",
            { ...params, allowed: columnList(params.allowed) },
          );
        case "number":
          return t("{{field}} “{{value}}” is not a number", params);
        case "integer":
          return t("{{field}} “{{value}}” is not a whole number", params);
        case "boolean":
          return t("{{field}} “{{value}}” is not true or false", params);
        case "range": {
          // The API sends `min`/`max` only for the bound the template declares, and fires
          // `range` when EITHER bound is violated — so a template with a minimum alone must
          // not render a literal `{{max}}`. With neither bound (a stale or unusual server)
          // the value is still not an acceptable whole number, so say that.
          const hasMin = params.min !== undefined && params.min !== null;
          const hasMax = params.max !== undefined && params.max !== null;
          if (hasMin && hasMax) {
            return t(
              "{{field}} “{{value}}” is outside {{min}} to {{max}}",
              params,
            );
          }
          if (hasMin) {
            return t("{{field}} “{{value}}” is below {{min}}", params);
          }
          if (hasMax) {
            return t("{{field}} “{{value}}” is above {{max}}", params);
          }
          return t("{{field}} “{{value}}” is not a whole number", params);
        }
        default:
          return entry.code;
      }
    // The WI format creates a row's location on the way. These three skip the LOCATION, not
    // the deployment, so each says the deployment was still imported.
    case "location_no_coordinates":
      return t(
        "{{field}} is blank, so no location was created for “{{location_name}}”; the deployment was imported without one",
        params,
      );
    case "location_invalid_coordinates":
      return t(
        "{{field}} “{{value}}” is not a coordinate akili can read, so no location was created for “{{location_name}}”. Use decimal degrees from {{min}} to {{max}}.",
        params,
      );
    case "location_name_too_long":
      return t(
        "location_name is {{actual}} characters, but akili allows {{limit}}, so no location was created; the deployment was imported without one",
        params,
      );
    default:
      return entry.code;
  }
};
